import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api, formatApiError } from "@/lib/api";
import {
    X, Copy, Check, Eye, EyeOff, ExternalLink, ServerCog,
    ShieldCheck, ShieldAlert, RefreshCcw, AlertTriangle,
} from "lucide-react";

/**
 * Stalwart-Webadmin-Zugangsdaten (nur Superadmin, nur Souvera-Clouds).
 *
 *   * Ruft beim Öffnen `GET /api/admin/clouds/{id}/stalwart-admin` auf (reiner
 *     Read — KEINE Aktivierung/Nebenwirkung, anders als ncadmin).
 *   * Zeigt die IP-beschränkte Admin-URL (dedizierte Subdomain), erlaubte IPs
 *     und die gespeicherten Stalwart-Admin-Zugangsdaten (aufdeckbar/kopierbar).
 *   * „LB-Routen sync" pusht/entfernt die Caddy-Routen fleet-weit.
 */
export default function StalwartAdminCredentialsModal({ cloudId, onClose }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pwVisible, setPwVisible] = useState(false);
    const [copied, setCopied] = useState(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);

    const load = async () => {
        try {
            const r = await api.get(`/api/admin/clouds/${cloudId}/stalwart-admin`);
            setData(r.data);
            setLoading(false);
        } catch (e) {
            setErr(formatApiError(e));
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cloudId]);

    useEffect(() => {
        const handler = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onClose]);

    const copy = async (label, text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(label);
            setTimeout(() => setCopied(null), 1500);
        } catch (e) { /* kein Clipboard in unsicherem Kontext */ }
    };

    const resync = async () => {
        setBusy(true); setErr(null); setMsg(null);
        try {
            const r = await api.post(`/api/admin/lb/stalwart-admin/sync`);
            const d = r.data || {};
            setMsg(d.enabled
                ? `Routes updated (${d.applied || 0} LB, ${d.routes || 0} cloud(s)).`
                : `Feature off (allowlist empty) — ${d.removed || 0} route(s) removed.`);
            await load();
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const enabled = data?.enabled;

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={onClose}
            data-testid="stalwart-admin-modal-overlay"
        >
            <div
                className="bg-zinc-950 border border-zinc-800 max-w-lg w-full shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                data-testid="stalwart-admin-modal"
            >
                <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
                    <div className="flex items-center gap-2">
                        <ServerCog size={16} className="text-sky-400" />
                        <h2 className="cm-h3">Stalwart webadmin access</h2>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="stalwart-admin-close-button"
                        className="text-zinc-400 hover:text-zinc-200"
                        aria-label="close"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5 space-y-4 text-sm">
                    {loading && (
                        <div className="text-zinc-400" data-testid="stalwart-admin-loading">
                            Loading credentials…
                        </div>
                    )}

                    {err && (
                        <div
                            className="bg-[#3a1010] border border-[#7a2a2a] p-3 text-xs text-[#ffaaaa] font-mono"
                            data-testid="stalwart-admin-error"
                        >
                            {err}
                        </div>
                    )}

                    {data && (
                        <>
                            <div
                                className={`flex items-center gap-2 px-3 py-2 text-xs border ${enabled
                                    ? "bg-emerald-900/20 border-emerald-700/50 text-emerald-200"
                                    : "bg-amber-900/15 border-amber-700/40 text-amber-200"}`}
                                data-testid="stalwart-admin-status"
                            >
                                {enabled ? <ShieldCheck size={14} className="text-emerald-300" />
                                         : <ShieldAlert size={14} className="text-amber-300" />}
                                {enabled ? (
                                    <span>IP-restricted access <strong>active</strong> · {data.allowlist?.length || 0} IP rule(s)</span>
                                ) : (
                                    <span>Access <strong>disabled</strong> — set the allowlist under Infrastructure → Settings → <span className="font-mono">stalwart_admin_ip_allowlist</span>.</span>
                                )}
                            </div>

                            {!data.reachable && (
                                <div className="flex items-center gap-2 bg-amber-900/10 border border-amber-800/40 px-3 py-2 text-xs text-amber-200/90">
                                    <AlertTriangle size={13} className="text-amber-300" />
                                    Stalwart VM of this cloud still unknown (`souvera_stalwart_ip` missing) — route only takes effect after provisioning.
                                </div>
                            )}

                            <CredRow
                                testidBase="stalwart-admin-url"
                                label="Admin URL"
                                value={data.url}
                                copy={() => copy("url", data.url)}
                                copied={copied === "url"}
                                trailing={enabled ? (
                                    <a href={data.url} target="_blank" rel="noreferrer noopener"
                                       className="text-cm-brand hover:text-cm-brand" title="open in new tab">
                                        <ExternalLink size={13} />
                                    </a>
                                ) : null}
                            />

                            <CredRow
                                testidBase="stalwart-admin-allowlist"
                                label="Allowed IPs"
                                value={data.allowlist?.length ? data.allowlist.join(", ") : "— none (feature off)"}
                                copy={data.allowlist?.length ? () => copy("allow", data.allowlist.join(", ")) : undefined}
                                copied={copied === "allow"}
                            />

                            <CredRow
                                testidBase="stalwart-admin-username"
                                label="Login user"
                                value={data.admin_user}
                                copy={() => copy("user", data.admin_user)}
                                copied={copied === "user"}
                            />

                            <CredRow
                                testidBase="stalwart-admin-password"
                                label="Login password"
                                value={
                                    !data.has_password
                                        ? "(not stored)"
                                        : pwVisible
                                            ? data.admin_password
                                            : "•".repeat((data.admin_password || "").length || 12)
                                }
                                copy={!data.has_password ? undefined : () => copy("password", data.admin_password)}
                                copied={copied === "password"}
                                trailing={!data.has_password ? null : (
                                    <button
                                        type="button"
                                        onClick={() => setPwVisible((v) => !v)}
                                        data-testid="stalwart-admin-password-toggle"
                                        className="text-zinc-400 hover:text-zinc-100"
                                            title={pwVisible ? "hide" : "show"}
                                    >
                                        {pwVisible ? <EyeOff size={13} /> : <Eye size={13} />}
                                    </button>
                                )}
                            />

                            <div className="flex items-center justify-between gap-3 pt-2 border-t border-zinc-800">
                                <div className="text-[11px] text-zinc-500 leading-relaxed">
                                    IP-restricted admin access — do not give to customers. Reachable only from the allowed IPs.
                                </div>
                                <button
                                    data-testid="stalwart-admin-sync"
                                    onClick={resync}
                                    disabled={busy}
                                    className="cm-btn text-xs px-2.5 py-1.5 disabled:opacity-50 flex items-center gap-1.5 shrink-0"
                                >
                                    <RefreshCcw size={12} className={busy ? "animate-spin" : ""} />
                                    {busy ? "Sync…" : "Sync LB routes"}
                                </button>
                            </div>
                            {msg && <div className="text-xs text-emerald-400" data-testid="stalwart-admin-msg">{msg}</div>}
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}

function CredRow({ label, value, copy, copied, trailing, testidBase }) {
    return (
        <div className="space-y-1">
            <div className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</div>
            <div
                className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 px-3 py-2"
                data-testid={testidBase}
            >
                <span
                    className="flex-1 font-mono text-xs text-zinc-100 break-all"
                    data-testid={`${testidBase}-value`}
                >
                    {value}
                </span>
                {copy && (
                    <button
                        type="button"
                        onClick={copy}
                        data-testid={`${testidBase}-copy-button`}
                        className="text-zinc-400 hover:text-zinc-100"
                        title="copy"
                    >
                        {copied ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
                    </button>
                )}
                {trailing}
            </div>
        </div>
    );
}
